import JSZip from "jszip";
import type { AssignmentResponse } from "../api/types";
import { fetchKretsMembers, type KretsMember } from "../api/kretsMembers";
import { mapboxAccessToken, printMapStyle } from "../map/config";

// Mapbox Static Images API max is 1280px per side; use @2x for doubled output (2560×1812)
const A4_LANDSCAPE_WIDTH = 1280;
const A4_LANDSCAPE_HEIGHT = 906;
const RETINA_SUFFIX = "@2x";
const DEFAULT_PADDING = 80;

type ExportProgress = {
    current: number;
    total: number;
    name: string;
};

type ExportResult = {
    exported: number;
    skipped: number;
};

const slugify = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
        .slice(0, 80);

const toPolygonFeatureCollection = (assignment: AssignmentResponse) => {
    const coordinates = assignment.area?.coordinates;
    if (!coordinates || coordinates.length === 0) {
        return null;
    }

    return {
        type: "FeatureCollection",
        features: [
            {
                type: "Feature",
                properties: {
                    assignmentId: assignment.id,
                    assignmentName: assignment.name,
                    stroke: "#114838",
                    "stroke-width": 3,
                    "stroke-opacity": 1,
                    fill: "#4a7a64",
                    "fill-opacity": 0.18
                },
                geometry: {
                    type: "Polygon",
                    coordinates
                }
            }
        ]
    };
};

const createStaticMapUrl = (assignment: AssignmentResponse): string | null => {
    const featureCollection = toPolygonFeatureCollection(assignment);
    if (!featureCollection) {
        return null;
    }

    const overlay = `geojson(${encodeURIComponent(JSON.stringify(featureCollection))})`;
    const styleId = printMapStyle.replace(/^mapbox:\/\/styles\//, "");
    const url = new URL(
        `https://api.mapbox.com/styles/v1/${styleId}/static/${overlay}/auto/${A4_LANDSCAPE_WIDTH}x${A4_LANDSCAPE_HEIGHT}${RETINA_SUFFIX}`
    );

    url.searchParams.set("padding", String(DEFAULT_PADDING));
    url.searchParams.set("logo", "false");
    url.searchParams.set("attribution", "false");
    url.searchParams.set("access_token", mapboxAccessToken);
    return url.toString();
};

const fileName = (assignment: AssignmentResponse, index: number): string => {
    const safeName = slugify(assignment.name) || `assignment-${index + 1}`;
    return `${String(index + 1).padStart(4, "0")}-${safeName}-${assignment.id}.png`;
};

const renderMapWithTextOverlay = (
    imageUrl: string,
    assignment: AssignmentResponse,
    kretsMember: KretsMember | undefined
): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d")!;
            ctx.drawImage(img, 0, 0);

            // Single-row bar: name (left) | address (center) | målantal (right)
            const addressLine = kretsMember?.homeAddress ?? assignment.distributorName ?? null;
            const targetLine = assignment.targetAddressCount != null
                ? `Målantal: ${assignment.targetAddressCount}`
                : null;

            const barH = 90;
            const barY = canvas.height - barH;
            const midY = barY + barH / 2;
            const pad = 48;

            ctx.fillStyle = "rgba(255, 255, 255, 0.90)";
            ctx.fillRect(0, barY, canvas.width, barH);

            ctx.fillStyle = "#114838";
            ctx.textBaseline = "middle";

            // Left: area name (bold)
            ctx.font = "bold 50px sans-serif";
            ctx.textAlign = "left";
            ctx.fillText(assignment.name, pad, midY);

            // Center: address
            if (addressLine) {
                ctx.font = "40px sans-serif";
                ctx.textAlign = "center";
                ctx.fillText(addressLine, canvas.width / 2, midY);
            }

            // Right: target count
            if (targetLine) {
                ctx.font = "bold 50px sans-serif";
                ctx.textAlign = "right";
                ctx.fillText(targetLine, canvas.width - pad, midY);
            }

            ctx.textAlign = "left";

            canvas.toBlob(
                (blob) => (blob ? resolve(blob) : reject(new Error("canvas toBlob misslyckades"))),
                "image/png"
            );
        };
        img.onerror = () => reject(new Error("Kunde inte ladda kartbild från Mapbox"));
        img.src = imageUrl;
    });
};

const triggerDownload = (blob: Blob, fileNameValue: string): void => {
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = fileNameValue;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
};

export const exportAssignmentsAsA4Images = async (
    assignments: AssignmentResponse[],
    onProgress: (progress: ExportProgress) => void
): Promise<ExportResult> => {
    if (!mapboxAccessToken) {
        throw new Error("Mapbox token saknas. Sätt VITE_MAPBOX_TOKEN eller centerreach.mapboxToken.");
    }

    // Pre-fetch krets members so we can look up home addresses by ID.
    const kretsMembersById = new Map<string, KretsMember>();
    try {
        const members = await fetchKretsMembers();
        for (const m of members) {
            kretsMembersById.set(m.id, m);
        }
    } catch {
        // Address lookup unavailable — export continues without addresses.
    }

    const zip = new JSZip();
    let exported = 0;
    let skipped = 0;

    for (let i = 0; i < assignments.length; i += 1) {
        const assignment = assignments[i];
        onProgress({
            current: i + 1,
            total: assignments.length,
            name: assignment.name
        });

        const url = createStaticMapUrl(assignment);
        if (!url) {
            skipped += 1;
            continue;
        }

        let blob: Blob;
        try {
            const kretsMember = assignment.kretsMemberId
                ? kretsMembersById.get(assignment.kretsMemberId)
                : undefined;
            blob = await renderMapWithTextOverlay(url, assignment, kretsMember);
        } catch (error) {
            console.warn(
                `[export] Mapbox static image failed for "${assignment.name}":`,
                error instanceof Error ? error.message : error
            );
            skipped += 1;
            continue;
        }
        zip.file(fileName(assignment, i), blob);
        exported += 1;
    }

    const archiveBlob = await zip.generateAsync({ type: "blob" });
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    triggerDownload(archiveBlob, `assignments-a4-landscape-${timestamp}.zip`);

    return { exported, skipped };
};

